import React, { useState, useEffect, useRef, useCallback } from "react";
import { FaSearch } from "react-icons/fa";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { UI_CONSTANTS } from "../../utils/constants";
import { getCityId, getCityName, parseCityList } from "../../utils/cityList";

const CitySearchSelect = ({
  value,
  onChange,
  label = "City Name",
  placeholder = "Select city",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const fetchCities = useCallback(async (search) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) {
        params.append("search", search.trim());
      }
      const query = params.toString();
      const url = query ? `${endpoints.cityList}?${query}` : endpoints.cityList;
      const response = await handleApiCall("get", url);

      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        setCities(parseCityList(response?.data?.data));
      } else {
        setCities([]);
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
      setCities([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const timer = setTimeout(() => {
      fetchCities(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, isOpen, fetchCities]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = () => {
    const nextOpen = !isOpen;
    setIsOpen(nextOpen);
    if (nextOpen) {
      fetchCities(searchTerm);
    }
  };

  const handleSelect = (city) => {
    onChange({
      id: getCityId(city),
      name: getCityName(city),
    });
    setIsOpen(false);
    setSearchTerm("");
  };

  return (
    <div className="form-group position-relative mb-0" ref={dropdownRef}>
      {label ? <label className="d-block mb-1">{label}</label> : null}
      <button
        type="button"
        className="form-control city-search-select__trigger text-left d-flex align-items-center justify-content-between w-100"
        onClick={handleToggle}
      >
        <span className={value?.name ? "" : "text-muted"}>{value?.name || placeholder}</span>
        <span className="ml-2">&#9662;</span>
      </button>
      {isOpen && (
        <div
          className="position-absolute rounded-3 shadow p-3 mt-1 w-100 city-search-select__menu"
          style={{ zIndex: 1050 }}
        >
          <div className="city-search-select__search">
            <FaSearch className="city-search-select__search-icon" aria-hidden />
            <input
              type="text"
              className="city-search-select__search-input"
              placeholder="Search city"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>
          <div style={{ maxHeight: "200px", overflowY: "auto" }}>
            {loading ? (
              <p className="mb-0 small text-muted px-2 py-1">Loading...</p>
            ) : cities.length === 0 ? (
              <p className="mb-0 small text-muted px-2 py-1">No cities found</p>
            ) : (
              cities.map((city) => {
                const cityId = getCityId(city);
                return (
                  <button
                    key={cityId || getCityName(city)}
                    type="button"
                    className={`btn btn-link btn-block text-left py-2 px-2 mb-0 city-search-select__option ${
                      value?.id === cityId ? "font-weight-bold" : ""
                    }`}
                    onClick={() => handleSelect(city)}
                  >
                    {getCityName(city)}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CitySearchSelect;
